import { useAuth, WithAuthorization } from "../../context";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import { Button, Center, Icon, Tab, TabList, TabPanel, TabPanels, Tabs, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import * as React from "react";
import { useMemo, useState } from "react";
import { DataTable } from "../../components/DataTable";
import { FaEye, FaPen } from "react-icons/fa";
import { useGetOwnedSupplierQuestionnaires } from "../../hooks/query";
import { mapSupplierQuestionnairesData, QUESTIONNAIRE_TYPES } from "./helpers";
import { QUESTIONNAIRE_STATUSES, showErrorToast, SUPPLIER_TYPES } from "../../utils";
import { hideAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "../../config";

function SupplierQuestionnaires() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierQuestionnaires"
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const { user, userPermissions } = useAuth();
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);

  const {
    data: supplierQuestionnaires,
    isLoading
  } = useGetOwnedSupplierQuestionnaires(tabIndex === 0 ?
    `?status=${QUESTIONNAIRE_STATUSES.NEW}&status=${QUESTIONNAIRE_STATUSES.IN_PROGRESS}` : tabIndex === 1 ?
      `?status=${QUESTIONNAIRE_STATUSES.FINALIZED}` : "", {
    onError: error => error.response?.status !== 404 ?
      showErrorToast(toast, error.response?.data?.parameters?.message ?? error.message) : null,
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const columns = useMemo(() => {
      function handleAction(data) {
        const questionnaireId = data.id;
        if (data.type === QUESTIONNAIRE_TYPES.GHG_EMISSIONS ||
          data.type === QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN) {
          navigate(`${routes.supplierQuestionnaire}?id=${questionnaireId}`);
        } else {
          navigate(`${routes.supplierPhysicalRiskQuestionnaire}?id=${questionnaireId}`);
        }
      }

      return [
        {
          header: t("gridColumns.assignee"),
          accessorKey: "assigneeName",
          size: 100
        },
        {
          header: t("gridColumns.period"),
          accessorKey: "period",
          size: 50
        },
        {
          header: t("gridColumns.type"),
          accessorKey: "mappedType",
          size: 80
        },
        {
          header: t("gridColumns.status"),
          accessorKey: "mappedStatus",
          size: 50
        },
        {
          header: t("gridColumns.endDate"),
          accessorKey: "endDate",
          size: 60
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("gridColumns.actions")}</b></div>,
          accessorKey: "action",
          size: 20,
          cell: row => {
            const { row: { original: rowData } } = row;
            const tooltipAction =
              rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED || !rowData.isAssignedToCurrentUser ||
              (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER)) ?
                t("buttons.review") : t("buttons.edit");
            const tooltipQuestionnaireType =
              t(`questionnaireTypes.${rowData.type}`);

            return <Button
              onClick={() => handleAction(rowData)}
              variant="ghost"
              title={`${tooltipAction} ${tooltipQuestionnaireType} ${t("questionnaire")}`}
            >
              <Icon
                as={rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED || !rowData.isAssignedToCurrentUser ||
                (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER)) ?
                  FaEye : FaPen
                }
                inlineSize="4"
                blockSize="4"
                color={rowData.type === QUESTIONNAIRE_TYPES.GHG_EMISSIONS ? "green" :
                  rowData.type === QUESTIONNAIRE_TYPES.PHYSICAL_RISK ? "blue" : "esg_decarb"}
                opacity="0.7"
              />
            </Button>;
          }
        }
      ];
    }, [t, navigate, userPermissions]
  );

  return (
    <Page title={t("page.title")} subTitle={t("page.subTitle")}>
      <Card>
        <Tabs colorScheme="customScheme" index={tabIndex} onChange={setTabIndex} isLazy>
          <Center>
            <TabList borderBottom="none" marginBottom={4}>
              <Tab _focus={{ boxShadow: "none" }}>{t("tabs.newInProgress")}</Tab>
              <Tab _focus={{ boxShadow: "none" }}>{t("tabs.finalised")}</Tab>
            </TabList>
          </Center>
          <TabPanels>
            <TabPanel padding="0">
              <DataTable
                data={mapSupplierQuestionnairesData(supplierQuestionnaires, user)}
                columns={columns}
                isLoading={isLoading}
                marginBlockEnd={4}
                containerHeight="var(--chakra-sizes-2xl)"
              />
            </TabPanel>
            <TabPanel padding="0">
              <DataTable
                data={mapSupplierQuestionnairesData(supplierQuestionnaires, user)}
                columns={columns}
                isLoading={isLoading}
                marginBlockEnd={4}
                containerHeight="var(--chakra-sizes-2xl)"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Card>
    </Page>
  );
}

export default WithAuthorization(SupplierQuestionnaires);